import { GameController } from "../game/stateMachine";
import { AudioManager } from "../game/audio";
import type { AuthManager } from "../auth/authManager";
import type { GameInput, UiAction } from "../game/input";
import { MirrorInputAdapter } from "./mirrorInputAdapter";
import { MatchRealtimeSync } from "./realtimeSync";
import { clearActiveMatch } from "./matchSessionStore";
import type {
  DoomDeathPayload,
  GameEventEnvelope,
  GameEventType,
  HeartbeatPayload,
  LobbySettings,
  MatchSummary,
  OpponentStats,
  RotationPayload,
  SnapshotPayload,
  SpecialTapPayload,
  SwapPayload,
  TrapKind,
  TrapSendPayload,
  VersusMatchState
} from "./types";
import {
  DISCONNECT_FORFEIT_SEC,
  TRAP_CONFIGS,
  TRAP_GLOBAL_COOLDOWN_SEC,
  VERSUS_DEBUG_LONG_DOOM,
  SNAPSHOT_REQUEST_COOLDOWN_SEC,
  REMOTE_GAMEPLAY_EVENT_BUFFER_MAX,
  REMOTE_TRAP_EVENT_BUFFER_MAX,
  REMOTE_GAMEPLAY_GAP_TIMEOUT_SEC,
  DEFERRED_SNAPSHOT_MAX_WAIT_SEC,
  SNAPSHOT_INTERVAL_HEALTHY_SEC,
  SNAPSHOT_INTERVAL_DEGRADED_SEC,
  HEARTBEAT_FULL_POWERUP_EVERY,
  RECENT_TRAP_SNAPSHOT_MAX,
  SYNC_SEND_FAILURE_DEGRADED_THRESHOLD,
  SYNC_RESYNC_DEGRADED_THRESHOLD,
  SYNC_STABLE_WINDOW_SEC
} from "./versusConstants";
import { doomClockCapForLevel, versusScoreTargetForLevel } from "../game/levelConfig";
import { processPendingTraps } from "./trapSystem";
import { getCachedEquippedCharacter } from "../economy/inventoryManager";

export interface VersusControllerCallbacks {
  onMatchEnd: (winnerId: string | null, result: string) => void;
  onVersusAction?: (action: UiAction) => void;
}

export interface VersusSyncDebugState {
  resyncCount: number;
  staleDropCount: number;
  trapReplayCount: number;
  dedupeDropCount: number;
}

const ORDERED_GAMEPLAY_EVENT_TYPES: ReadonlySet<GameEventType> = new Set([
  "SWAP",
  "ROTATION",
  "POWERUP_BUY",
  "POWERUP_USE",
  "SPECIAL_TAP",
  "GROOVE_ACTIVATE",
  "DOOM_DEATH",
  "FORFEIT"
]);

export class VersusController {
  readonly localController: GameController;
  readonly mirrorController: GameController;
  readonly mirrorInput: MirrorInputAdapter;

  private readonly sync: MatchRealtimeSync | null;
  private readonly callbacks: VersusControllerCallbacks;

  private matchState: VersusMatchState;
  private snapshotTimer = 0;
  private disconnectTimer: number | null = null;
  private fogRemainingSec = 0;
  private lastSentTrap: { kind: TrapKind; ageSec: number } | null = null;
  private ended = false;
  private deferredSnapshot: SnapshotPayload | null = null;
  private deferredSnapshotAgeSec = 0;
  private deferredSnapshotSeq = 0;
  private localDoomDead = false;
  private remoteDoomDead = false;
  private localDoomDeathSent = false;
  private localDoomFinalScore: number | null = null;
  private remoteDoomFinalScore: number | null = null;

  private localGameplaySeq = 0;
  private expectedRemoteGameplaySeq = 1;
  private remoteGameplayBuffer = new Map<number, GameEventEnvelope>();
  private remoteGameplayGapAgeSec = 0;
  private pendingRemoteSpecialTaps: SpecialTapPayload[] = [];
  private deferredRemoteGameplayEvents: GameEventEnvelope[] = [];
  private snapshotRequestCooldownSec = 0;
  private snapshotSeq = 0;
  private heartbeatSeq = 0;
  private heartbeatEmitCount = 0;
  private lastHeartbeatPowerupsKey = "";
  private expectedRemoteTrapSeq = 1;
  private lastAppliedRemoteTrapSeq = 0;
  private remoteBotTrapSeq = 0;
  private remoteBotTrapCooldownRemainingSec = 0;
  private remoteTrapBuffer = new Map<number, { payload: TrapSendPayload; event: GameEventEnvelope }>();
  private localRecentTraps: TrapSendPayload[] = [];
  private lastHeartbeatSeqApplied = 0;
  private lastSnapshotSeqApplied = 0;
  private recentEventIds = new Map<string, number>();
  private localEventIdCounter = 0;
  private dedupeDropCount = 0;
  private lastRemoteEventAtMs = Date.now();
  private stableHealthTimerSec = 0;

  constructor(
    audio: AudioManager,
    authManager: AuthManager,
    matchId: string,
    lobbyId: string | null,
    seed: number,
    localPlayerId: string,
    remotePlayerId: string,
    remoteDisplayName: string,
    settings: LobbySettings,
    callbacks: VersusControllerCallbacks,
    offline = false,
    externalMirrorInput?: MirrorInputAdapter,
    remoteEquippedSkin = "1"
  ) {
    this.callbacks = callbacks;

    this.matchState = {
      matchId,
      lobbyId,
      seed,
      localPlayerId,
      remotePlayerId,
      settings,
      opponentStats: {
        score: 0,
        level: 1,
        levelProgressPct: 0,
        equippedSkin: remoteEquippedSkin,
        doomPct: 0,
        groovePct: 0,
        grooveActive: false,
        grooveReady: false,
        pendingRotation: null,
        rotationKeys: 0,
        cash: 0,
        powerups: createEmptyPowerupInventory(),
        displayName: remoteDisplayName,
        connected: true
      },
      trapCooldownRemainingSec: 0,
      trapSeq: 0,
      pendingTraps: [],
      syncHealth: "HEALTHY",
      syncMetrics: {
        resyncCount: 0,
        lastRemoteEventAgeSec: 0,
        sendFailCount: 0,
        staleDropCount: 0,
        trapReplayCount: 0
      },
      disconnectCountdownSec: null,
      suddenDeath: null,
      winnerId: null,
      result: null
    };

    this.localController = new GameController(audio, authManager, {
      sessionMode: "versus",
      allowMusicIntentChanges: true,
      swapBufferMode: "live_latest"
    });
    this.mirrorController = new GameController(audio, authManager, {
      sessionMode: "versus",
      allowMusicIntentChanges: false,
      swapBufferMode: "replay_fifo"
    });
    this.mirrorInput = externalMirrorInput ?? new MirrorInputAdapter();

    this.localController.startNewRunFromSeed(seed, {
      startLevel: settings.startLevel,
      doomClockStartSec: settings.doomClockStartSec
    });
    this.localController.phase = "RUN_PLAYABLE";

    this.mirrorController.startNewRunFromSeed(seed, {
      startLevel: settings.startLevel,
      doomClockStartSec: settings.doomClockStartSec
    });
    this.mirrorController.phase = "RUN_PLAYABLE";

    if (VERSUS_DEBUG_LONG_DOOM) {
      if (this.localController.run) this.localController.run.doomClockSec = 600;
      if (this.mirrorController.run) this.mirrorController.run.doomClockSec = 600;
    }

    this.localController.onGameEvent = (type, payload) => {
      if (type === "SWAP") {
        this.emitSwap(
          payload.fromRow as number,
          payload.fromCol as number,
          payload.toRow as number,
          payload.toCol as number
        );
      } else if (type === "ROTATION") {
        this.emitRotation(payload.dir as "LEFT" | "RIGHT");
      } else if (type === "GROOVE_ACTIVATE") {
        this.emitGrooveActivate();
      } else if (type === "POWERUP_BUY") {
        this.emitPowerupBuy(payload.kind as string);
      } else if (type === "POWERUP_USE") {
        this.emitPowerupUse(
          payload.kind as string,
          payload.targetRow as number | undefined,
          payload.targetCol as number | undefined,
          payload.secondRow as number | undefined,
          payload.secondCol as number | undefined
        );
      } else if (type === "SPECIAL_TAP") {
        this.emitSpecialTap(
          payload.row as number,
          payload.col as number,
          payload.special as SpecialTapPayload["special"]
        );
      }
    };
    this.localController.onVersusAction = (action) => {
      this.handleLocalVersusAction(action);
    };

    if (offline) {
      this.sync = null;
    } else {
      this.sync = new MatchRealtimeSync();
      this.sync.subscribe(
        matchId,
        localPlayerId,
        remotePlayerId,
        {
          onRemoteEvent: (event) => this.handleRemoteEvent(event),
          onRemoteDisconnect: () => this.handleRemoteDisconnect(),
          onRemoteReconnect: () => this.handleRemoteReconnect(),
          onChannelStatusChanged: (status) => this.handleChannelStatusChanged(status),
          onSendFailure: (_eventType, reason) => this.handleSendFailure(reason)
        },
        () => this.buildHeartbeatPayload()
      );
    }
  }

  update(dt: number, localInput: GameInput): void {
    if (this.ended) return;

    this.localController.update(dt, localInput);
    this.mirrorController.update(dt, this.mirrorInput);
    this.drainPendingRemoteSpecialTaps();

    if (this.snapshotRequestCooldownSec > 0) {
      this.snapshotRequestCooldownSec = Math.max(0, this.snapshotRequestCooldownSec - dt);
    }
    this.matchState.syncMetrics.lastRemoteEventAgeSec =
      Math.max(0, (Date.now() - this.lastRemoteEventAtMs) / 1000);
    this.updateSyncHealthStability(dt);

    this.updateRemoteGameplayGap(dt);

    if (this.deferredSnapshot != null) {
      this.deferredSnapshotAgeSec += dt;
      const shouldForceApply = this.deferredSnapshotAgeSec >= DEFERRED_SNAPSHOT_MAX_WAIT_SEC;
      if (shouldForceApply || !this.mirrorController.isBusy()) {
        this.lastSnapshotSeqApplied = Math.max(this.lastSnapshotSeqApplied, this.deferredSnapshotSeq);
        this.applySnapshot(this.deferredSnapshot);
        this.deferredSnapshot = null;
        this.deferredSnapshotAgeSec = 0;
        this.deferredSnapshotSeq = 0;
      }
    }

    this.snapshotTimer += dt;
    const snapshotIntervalSec = this.matchState.syncHealth === "HEALTHY"
      ? SNAPSHOT_INTERVAL_HEALTHY_SEC
      : SNAPSHOT_INTERVAL_DEGRADED_SEC;
    if (this.snapshotTimer >= snapshotIntervalSec) {
      this.snapshotTimer -= snapshotIntervalSec;
      this.sendSnapshot();
    }

    if (this.disconnectTimer != null) {
      this.disconnectTimer -= dt;
      this.matchState.disconnectCountdownSec = Math.max(0, this.disconnectTimer);
      if (this.disconnectTimer <= 0) {
        this.endMatch(this.matchState.localPlayerId, "DISCONNECT");
      }
    }

    if (this.matchState.trapCooldownRemainingSec > 0) {
      this.matchState.trapCooldownRemainingSec = Math.max(
        0,
        this.matchState.trapCooldownRemainingSec - dt
      );
    }
    if (this.remoteBotTrapCooldownRemainingSec > 0) {
      this.remoteBotTrapCooldownRemainingSec = Math.max(
        0,
        this.remoteBotTrapCooldownRemainingSec - dt
      );
    }

    const fogAdd = processPendingTraps(this.localController, this.matchState);
    if (fogAdd > 0) {
      this.fogRemainingSec += fogAdd;
    }

    if (this.fogRemainingSec > 0) {
      this.fogRemainingSec = Math.max(0, this.fogRemainingSec - dt);
    }

    // Tick sent-trap FX timer (3 second visual)
    if (this.lastSentTrap != null) {
      this.lastSentTrap.ageSec += dt;
      if (this.lastSentTrap.ageSec >= 3) {
        this.lastSentTrap = null;
      }
    }

    this.syncOpponentStatsFromMirror();
    this.checkDeath();
    this.tickSuddenDeath(dt);
  }

  getMatchState(): VersusMatchState {
    return this.matchState;
  }

  getSyncDebugState(): VersusSyncDebugState {
    return {
      resyncCount: this.matchState.syncMetrics.resyncCount,
      staleDropCount: this.matchState.syncMetrics.staleDropCount,
      trapReplayCount: this.matchState.syncMetrics.trapReplayCount,
      dedupeDropCount: this.dedupeDropCount
    };
  }

  getOpponentStats(): OpponentStats {
    return this.matchState.opponentStats;
  }

  isFogActive(): boolean {
    return this.fogRemainingSec > 0;
  }

  getFogRemainingSec(): number {
    return this.fogRemainingSec;
  }

  getLastSentTrap(): { kind: TrapKind; ageSec: number } | null {
    return this.lastSentTrap;
  }

  areLocalTrapActionsEnabled(): boolean {
    if (this.ended) return false;
    if (!this.matchState.settings.trapsEnabled) return false;
    const run = this.localController.run;
    if (run == null) return false;
    if (this.localController.phase === "RUN_GAMEOVER") return false;
    if (run.doomClockSec <= 0) return false;
    return true;
  }

  getLocalFinalStats(): { score: number; level: number } {
    const run = this.localController.run;
    return { score: run?.totalScore ?? 0, level: run?.level ?? 1 };
  }

  getRemoteFinalStats(): { score: number; level: number } {
    this.syncOpponentStatsFromMirror();
    return {
      score: this.bestKnownRemoteScore(),
      level: this.bestKnownRemoteLevel()
    };
  }

  buildMatchSummary(durationSec: number): MatchSummary {
    const run = this.localController.run;
    return {
      matchId: this.matchState.matchId,
      playerId: this.matchState.localPlayerId,
      winnerId: this.matchState.winnerId,
      result: this.matchState.result!,
      score: run?.totalScore ?? 0,
      level: run?.level ?? 1,
      cashBalance: run?.cashBalance ?? 0,
      trapsSent: this.matchState.trapSeq,
      durationSec
    };
  }

  destroy(): void {
    this.sync?.unsubscribe();
    this.ended = true;
  }

  private handleLocalVersusAction(action: UiAction): void {
    switch (action) {
      case "TRAP_LOCKDOWN":
        this.castTrap("LOCKDOWN_CHAINS");
        return;
      case "TRAP_FOG":
        this.castTrap("FOG_OVERLAY");
        return;
      case "TRAP_COIN_TAX":
        this.castTrap("COIN_TAX");
        return;
      case "TRAP_GROOVE_DRAIN":
        this.castTrap("GROOVE_DRAIN");
        return;
      case "TRAP_DOOM_SURGE":
        this.castTrap("DOOM_SURGE");
        return;
      case "VERSUS_FORFEIT":
        this.forfeit();
        return;
      default:
        this.callbacks.onVersusAction?.(action);
    }
  }

  emitSwap(fromRow: number, fromCol: number, toRow: number, toCol: number): void {
    const payload: SwapPayload = { fromRow, fromCol, toRow, toCol };
    const seq = this.sync?.sendEvent("SWAP", payload);
    if (seq != null) this.recordLocalGameplaySeq(seq);
  }

  emitRotation(dir: "LEFT" | "RIGHT"): void {
    const payload: RotationPayload = { dir };
    const seq = this.sync?.sendEvent("ROTATION", payload);
    if (seq != null) this.recordLocalGameplaySeq(seq);
  }

  emitPowerupBuy(kind: string): void {
    const seq = this.sync?.sendEvent("POWERUP_BUY", { kind });
    if (seq != null) this.recordLocalGameplaySeq(seq);
  }

  emitPowerupUse(kind: string, targetRow?: number, targetCol?: number, secondRow?: number, secondCol?: number): void {
    const seq = this.sync?.sendEvent("POWERUP_USE", { kind, targetRow, targetCol, secondRow, secondCol });
    if (seq != null) this.recordLocalGameplaySeq(seq);
  }

  emitSpecialTap(row: number, col: number, special: SpecialTapPayload["special"]): void {
    const payload: SpecialTapPayload = { row, col, special };
    const seq = this.sync?.sendEvent("SPECIAL_TAP", payload);
    if (seq != null) this.recordLocalGameplaySeq(seq);
  }

  emitGrooveActivate(): void {
    const seq = this.sync?.sendEvent("GROOVE_ACTIVATE", {});
    if (seq != null) this.recordLocalGameplaySeq(seq);
  }

  forfeit(): void {
    const seq = this.sync?.sendEvent("FORFEIT", {});
    if (seq != null) this.recordLocalGameplaySeq(seq);
    this.sendSnapshot();
    this.endMatch(this.matchState.remotePlayerId, "FORFEIT");
  }

  castTrap(kind: TrapKind): boolean {
    if (!this.areLocalTrapActionsEnabled()) return false;
    if (this.matchState.trapCooldownRemainingSec > 0) return false;

    const config = TRAP_CONFIGS[kind];
    if (config == null) return false;

    const run = this.localController.run;
    if (run == null) return false;
    if (run.cashBalance < config.cost) return false;

    run.cashBalance -= config.cost;

    this.matchState.trapSeq += 1;
    this.matchState.trapCooldownRemainingSec = TRAP_GLOBAL_COOLDOWN_SEC;

    const payload: TrapSendPayload = {
      trap: kind,
      trapSeq: this.matchState.trapSeq
    };
    const seq = this.sync?.sendEvent("TRAP_SEND", payload);
    if (seq != null) this.recordLocalGameplaySeq(seq);
    this.recordLocalTrap(payload);

    this.lastSentTrap = { kind, ageSec: 0 };
    return true;
  }

  castRemoteBotTrap(kind: TrapKind): boolean {
    if (this.sync != null) return false;
    if (this.ended) return false;
    if (!this.matchState.settings.trapsEnabled) return false;
    if (this.remoteBotTrapCooldownRemainingSec > 0) return false;
    if (!this.isRemoteTrapSourceAlive()) return false;

    const localRun = this.localController.run;
    const remoteRun = this.mirrorController.run;
    if (localRun == null || remoteRun == null) return false;
    if (this.localController.phase === "RUN_GAMEOVER") return false;
    if (localRun.doomClockSec <= 0) return false;

    const config = TRAP_CONFIGS[kind];
    if (config == null) return false;
    if (remoteRun.cashBalance < config.cost) return false;

    remoteRun.cashBalance -= config.cost;
    this.matchState.opponentStats.cash = Math.max(0, Math.floor(remoteRun.cashBalance));

    this.remoteBotTrapSeq += 1;
    this.remoteBotTrapCooldownRemainingSec = TRAP_GLOBAL_COOLDOWN_SEC;

    const now = Date.now();
    const payload: TrapSendPayload = {
      trap: kind,
      trapSeq: this.remoteBotTrapSeq
    };
    this.handleRemoteEvent({
      protocolVersion: 2,
      eventId: `bot-trap:${this.remoteBotTrapSeq}`,
      sentAtMs: now,
      type: "TRAP_SEND",
      seq: this.remoteBotTrapSeq,
      ts: now,
      playerId: this.matchState.remotePlayerId,
      payload
    });
    return true;
  }

  private isRemoteTrapSourceAlive(): boolean {
    if (this.remoteDoomDead) return false;
    if (this.mirrorController.phase === "RUN_GAMEOVER") return false;
    const run = this.mirrorController.run;
    if (run != null && run.doomClockSec <= 0) return false;
    return true;
  }

  private handleRemoteEvent(event: GameEventEnvelope): void {
    if (this.ended) return;
    this.lastRemoteEventAtMs = Date.now();

    const eventId = this.extractEventId(event);
    if (this.recentEventIds.has(eventId)) {
      this.dedupeDropCount += 1;
      return;
    }
    this.rememberEventId(eventId);

    if (event.type === "HEARTBEAT") {
      this.handleRemoteHeartbeat(event);
      return;
    }

    if (event.type === "SNAPSHOT") {
      this.handleRemoteSnapshot(event);
      return;
    }

    if (event.type === "SNAPSHOT_REQUEST") {
      this.sendSnapshot();
      return;
    }

    if (event.type === "REJOIN_REQUEST") {
      this.handleRejoinRequest();
      return;
    }

    if (event.type === "TRAP_SEND") {
      this.handleRemoteTrapEvent(event);
      return;
    }

    if (ORDERED_GAMEPLAY_EVENT_TYPES.has(event.type)) {
      this.handleOrderedRemoteGameplayEvent(event);
    }
  }

  private handleRemoteHeartbeat(event: GameEventEnvelope): void {
    const p = event.payload as HeartbeatPayload;
    const seq = Math.max(1, Math.floor(p.heartbeatSeq ?? event.seq ?? 0));
    if (seq <= this.lastHeartbeatSeqApplied) {
      this.markStaleDrop();
      return;
    }
    this.lastHeartbeatSeqApplied = seq;

    this.matchState.opponentStats.score = p.score;
    this.matchState.opponentStats.level = p.level;
    this.matchState.opponentStats.levelProgressPct = clamp01(
      p.levelProgressPct ?? this.matchState.opponentStats.levelProgressPct
    );
    this.matchState.opponentStats.equippedSkin =
      p.equippedSkin ?? this.matchState.opponentStats.equippedSkin;
    this.matchState.opponentStats.doomPct = clamp01(p.doomPct);
    this.matchState.opponentStats.groovePct = clamp01(p.groovePct);
    this.matchState.opponentStats.grooveActive =
      p.grooveActive ?? this.matchState.opponentStats.grooveActive;
    this.matchState.opponentStats.grooveReady =
      p.grooveReady ?? this.matchState.opponentStats.grooveReady;
    this.matchState.opponentStats.pendingRotation = p.pendingRotation ?? null;
    this.matchState.opponentStats.rotationKeys = Math.max(
      0,
      Math.floor(p.rotationKeys ?? this.matchState.opponentStats.rotationKeys)
    );
    this.matchState.opponentStats.cash = Math.max(0, Math.floor(p.cash));
    if (p.powerups != null) {
      this.matchState.opponentStats.powerups = sanitizePowerupInventory(p.powerups);
    }
  }

  private handleRemoteSnapshot(event: GameEventEnvelope): void {
    const snapshot = event.payload as SnapshotPayload;
    const snapshotSeq = this.snapshotSeqFromPayload(snapshot, event.seq);
    if (snapshotSeq <= this.lastSnapshotSeqApplied) {
      this.markStaleDrop();
      return;
    }

    if (this.mirrorController.isBusy()) {
      if (snapshotSeq >= this.deferredSnapshotSeq) {
        this.deferredSnapshot = snapshot;
        this.deferredSnapshotSeq = snapshotSeq;
        this.deferredSnapshotAgeSec = 0;
      }
      return;
    }

    this.lastSnapshotSeqApplied = snapshotSeq;
    this.applySnapshot(snapshot);
  }

  private handleRemoteTrapEvent(event: GameEventEnvelope): void {
    if (!this.isRemoteTrapSourceAlive()) {
      return;
    }
    const payload = event.payload as TrapSendPayload;
    const trapSeq = Math.max(1, Math.floor(payload.trapSeq ?? event.seq ?? 0));
    if (trapSeq <= this.lastAppliedRemoteTrapSeq || trapSeq < this.expectedRemoteTrapSeq) {
      return;
    }

    if (trapSeq === this.expectedRemoteTrapSeq) {
      this.applyRemoteTrapPayload(payload, event, false);
      this.expectedRemoteTrapSeq += 1;
      this.flushRemoteTrapBuffer();
      return;
    }

    this.remoteTrapBuffer.set(trapSeq, { payload, event });
    if (this.remoteTrapBuffer.size > REMOTE_TRAP_EVENT_BUFFER_MAX) {
      this.remoteTrapBuffer.clear();
      this.requestSnapshotFromRemote("trap-buffer-overflow");
    }
  }

  private flushRemoteTrapBuffer(): void {
    while (true) {
      const queued = this.remoteTrapBuffer.get(this.expectedRemoteTrapSeq);
      if (queued == null) {
        break;
      }
      this.remoteTrapBuffer.delete(this.expectedRemoteTrapSeq);
      this.applyRemoteTrapPayload(queued.payload, queued.event, false);
      this.expectedRemoteTrapSeq += 1;
    }
  }

  private applyRemoteTrapPayload(payload: TrapSendPayload, event: GameEventEnvelope, replayed: boolean): void {
    if (!this.isRemoteTrapSourceAlive()) {
      return;
    }
    const trapSeq = Math.max(1, Math.floor(payload.trapSeq));
    if (trapSeq <= this.lastAppliedRemoteTrapSeq) {
      return;
    }
    this.matchState.pendingTraps.push({
      kind: payload.trap,
      seq: trapSeq,
      senderId: event.playerId,
      ts: event.ts
    });
    this.lastAppliedRemoteTrapSeq = trapSeq;
    if (replayed) {
      this.matchState.syncMetrics.trapReplayCount += 1;
    }
  }

  private extractEventId(event: GameEventEnvelope): string {
    if (event.eventId != null && event.eventId.length > 0) {
      return event.eventId;
    }
    const fallbackSeq = Number.isFinite(event.seq) ? Math.floor(event.seq) : ++this.localEventIdCounter;
    return `legacy:${event.playerId}:${fallbackSeq}`;
  }

  private rememberEventId(eventId: string): void {
    this.recentEventIds.delete(eventId);
    this.recentEventIds.set(eventId, Date.now());
    while (this.recentEventIds.size > 256) {
      const oldest = this.recentEventIds.keys().next().value;
      if (oldest == null) {
        break;
      }
      this.recentEventIds.delete(oldest);
    }
  }

  private snapshotSeqFromPayload(snapshot: SnapshotPayload, fallbackEventSeq: number): number {
    const raw = snapshot.snapshotSeq ?? fallbackEventSeq;
    if (!Number.isFinite(raw)) {
      return this.lastSnapshotSeqApplied + 1;
    }
    return Math.max(1, Math.floor(raw));
  }

  private markStaleDrop(): void {
    this.matchState.syncMetrics.staleDropCount += 1;
  }

  private handleOrderedRemoteGameplayEvent(event: GameEventEnvelope): void {
    if (event.gameplaySeq == null) {
      this.applyRemoteGameplayEvent(event);
      return;
    }
    const gameplaySeq = this.eventGameplaySeq(event);
    if (gameplaySeq < this.expectedRemoteGameplaySeq) {
      return;
    }

    if (gameplaySeq === this.expectedRemoteGameplaySeq) {
      this.applyRemoteGameplayEvent(event);
      this.expectedRemoteGameplaySeq += 1;
      this.remoteGameplayGapAgeSec = 0;
      this.flushRemoteGameplayBuffer();
      return;
    }

    this.remoteGameplayBuffer.set(gameplaySeq, event);
    if (this.remoteGameplayBuffer.size > REMOTE_GAMEPLAY_EVENT_BUFFER_MAX) {
      this.triggerGameplayResync("buffer-overflow");
    }
  }

  private eventGameplaySeq(event: GameEventEnvelope): number {
    const raw = event.gameplaySeq ?? event.seq;
    if (!Number.isFinite(raw)) {
      return this.expectedRemoteGameplaySeq;
    }
    return Math.max(1, Math.floor(raw));
  }

  private flushRemoteGameplayBuffer(): void {
    while (true) {
      const next = this.remoteGameplayBuffer.get(this.expectedRemoteGameplaySeq);
      if (next == null) {
        break;
      }
      this.remoteGameplayBuffer.delete(this.expectedRemoteGameplaySeq);
      this.applyRemoteGameplayEvent(next);
      this.expectedRemoteGameplaySeq += 1;
    }
    if (this.remoteGameplayBuffer.size === 0) {
      this.remoteGameplayGapAgeSec = 0;
    }
  }

  private applyRemoteGameplayEvent(event: GameEventEnvelope): void {
    if (event.type === "SPECIAL_TAP") {
      this.pendingRemoteSpecialTaps.push(event.payload as SpecialTapPayload);
      return;
    }
    if (this.pendingRemoteSpecialTaps.length > 0) {
      this.deferredRemoteGameplayEvents.push(event);
      return;
    }
    this.applyRemoteGameplayEventNow(event);
  }

  private applyRemoteGameplayEventNow(event: GameEventEnvelope): void {
    switch (event.type) {
      case "SWAP": {
        const p = event.payload as SwapPayload;
        this.mirrorInput.pushSwap(
          { row: p.fromRow, col: p.fromCol },
          { row: p.toRow, col: p.toCol }
        );
        break;
      }
      case "ROTATION": {
        const p = event.payload as RotationPayload;
        this.mirrorInput.pushRotation(p.dir);
        break;
      }
      case "POWERUP_BUY": {
        const p = event.payload as { kind: string };
        const armAction = powerupKindToArmAction(p.kind);
        if (armAction != null) {
          this.mirrorInput.pushAction(armAction);
        }
        break;
      }
      case "POWERUP_USE": {
        const p = event.payload as {
          targetRow?: number;
          targetCol?: number;
          secondRow?: number;
          secondCol?: number;
        };
        if (p.targetRow != null && p.targetCol != null) {
          this.mirrorInput.pushBoardClick({ row: p.targetRow, col: p.targetCol });
        }
        if (p.secondRow != null && p.secondCol != null) {
          this.mirrorInput.pushBoardClick({ row: p.secondRow, col: p.secondCol });
        }
        break;
      }
      case "GROOVE_ACTIVATE": {
        this.mirrorInput.pushAction("ACTIVATE_GROOVE");
        break;
      }
      case "DOOM_DEATH": {
        const payload = event.payload as DoomDeathPayload;
        this.registerRemoteDoomDeath(payload.finalScore);
        break;
      }
      case "FORFEIT": {
        this.endMatch(this.matchState.localPlayerId, "FORFEIT");
        break;
      }
      default:
        break;
    }
  }

  private drainPendingRemoteSpecialTaps(): void {
    if (
      this.pendingRemoteSpecialTaps.length > 0
      && this.mirrorInput.getQueueSize() === 0
      && !this.mirrorController.isBusy()
    ) {
      const nextTap = this.pendingRemoteSpecialTaps.shift();
      if (nextTap != null) {
        this.mirrorController.applySyncedSpecialTap(nextTap);
      }
    }

    if (
      this.pendingRemoteSpecialTaps.length === 0
      && this.deferredRemoteGameplayEvents.length > 0
      && this.mirrorInput.getQueueSize() === 0
      && !this.mirrorController.isBusy()
    ) {
      const deferred = [...this.deferredRemoteGameplayEvents];
      this.deferredRemoteGameplayEvents.length = 0;
      for (const event of deferred) {
        this.applyRemoteGameplayEventNow(event);
      }
    }
  }

  private updateRemoteGameplayGap(dt: number): void {
    const hasGap =
      this.remoteGameplayBuffer.size > 0
      && !this.remoteGameplayBuffer.has(this.expectedRemoteGameplaySeq);
    if (!hasGap) {
      this.remoteGameplayGapAgeSec = 0;
      return;
    }

    this.remoteGameplayGapAgeSec += dt;
    if (this.remoteGameplayGapAgeSec >= REMOTE_GAMEPLAY_GAP_TIMEOUT_SEC) {
      this.triggerGameplayResync("gap-timeout");
    }
  }

  private triggerGameplayResync(reason: string): void {
    this.remoteGameplayGapAgeSec = 0;
    this.remoteGameplayBuffer.clear();
    this.pendingRemoteSpecialTaps.length = 0;
    this.deferredRemoteGameplayEvents.length = 0;
    this.mirrorInput.flush();
    this.matchState.syncMetrics.resyncCount += 1;
    if (this.matchState.syncMetrics.resyncCount >= SYNC_RESYNC_DEGRADED_THRESHOLD) {
      this.setSyncHealth("DEGRADED");
    }
    this.requestSnapshotFromRemote(reason);
  }

  private requestSnapshotFromRemote(reason: string): void {
    if (this.snapshotRequestCooldownSec > 0) {
      return;
    }
    this.sync?.sendEvent("SNAPSHOT_REQUEST", {
      reason,
      expectedGameplaySeq: this.expectedRemoteGameplaySeq,
      expectedTrapSeq: this.expectedRemoteTrapSeq
    });
    this.snapshotRequestCooldownSec = SNAPSHOT_REQUEST_COOLDOWN_SEC;
  }

  private handleRemoteDisconnect(): void {
    this.matchState.opponentStats.connected = false;
    this.disconnectTimer = DISCONNECT_FORFEIT_SEC;
    this.matchState.disconnectCountdownSec = DISCONNECT_FORFEIT_SEC;
    this.setSyncHealth("RECONNECTING");
  }

  private handleRemoteReconnect(): void {
    this.matchState.opponentStats.connected = true;
    this.disconnectTimer = null;
    this.matchState.disconnectCountdownSec = null;
    this.setSyncHealth("DEGRADED");
    this.sync?.sendEvent("HEARTBEAT", this.buildHeartbeatPayload());
    this.sendSnapshot();
  }

  private buildHeartbeatPayload(): HeartbeatPayload {
    const run = this.localController.run;
    const localView = this.localController.getViewModel();
    const scoreTarget = versusScoreTargetForLevel(run?.level ?? 1);
    this.heartbeatSeq += 1;
    this.heartbeatEmitCount += 1;
    const levelProgressPct = run != null
      ? clamp01(run.levelScore / Math.max(1, scoreTarget))
      : 0;
    const powerups = sanitizePowerupInventory(run?.powerupInventory);
    const powerupKey = `${powerups.HINT}:${powerups.ROWRIP}:${powerups.COLCUT}:${powerups.TRANSMUTE}:${powerups.ZIGZAGBOLT}`;
    const includePowerups =
      powerupKey !== this.lastHeartbeatPowerupsKey
      || (this.heartbeatEmitCount % HEARTBEAT_FULL_POWERUP_EVERY) === 0;
    if (includePowerups) {
      this.lastHeartbeatPowerupsKey = powerupKey;
    }
    return {
      heartbeatSeq: this.heartbeatSeq,
      ackGameplaySeq: this.expectedRemoteGameplaySeq - 1,
      score: run?.totalScore ?? 0,
      level: run?.level ?? 1,
      levelProgressPct: quantize01(levelProgressPct),
      equippedSkin: getCachedEquippedCharacter().id ?? "1",
      doomPct: quantize01(run != null ? 1 - (run.doomClockSec / doomClockCapForLevel(run.level)) : 0),
      groovePct: quantize01(run?.groove?.groove ?? 0),
      grooveActive: run?.groove?.active ?? false,
      grooveReady: (run?.groove?.armed ?? false) && !(run?.groove?.active ?? false) && (run?.groove?.readyWindowSec ?? 0) > 0,
      pendingRotation: localView.pendingRotation,
      rotationKeys: run?.rotationKeys ?? 0,
      cash: run?.cashBalance ?? 0,
      powerups: includePowerups ? powerups : undefined
    };
  }

  private sendSnapshot(): void {
    if (this.localController.run == null) return;
    this.snapshotSeq += 1;
    const payload: SnapshotPayload = {
      run: this.localController.run,
      lastGameplaySeq: this.localGameplaySeq,
      snapshotSeq: this.snapshotSeq,
      lastTrapSeq: this.matchState.trapSeq,
      recentTraps: [...this.localRecentTraps]
    };
    this.sync?.sendEvent("SNAPSHOT", payload);
  }

  private handleRejoinRequest(): void {
    // Respond with both players' run states so the rejoining player can restore
    const localRun = this.localController.run;
    const mirrorRun = this.mirrorController.run;
    if (localRun == null || mirrorRun == null) return;
    this.sync?.sendEvent("REJOIN_SNAPSHOT", {
      opponentRun: localRun,
      rejoinRun: mirrorRun,
      matchState: {
        trapSeq: this.matchState.trapSeq,
        opponentStats: { ...this.matchState.opponentStats },
      },
    });
  }

  private applySnapshot(snapshot: SnapshotPayload): void {
    if (this.mirrorController.run == null) {
      return;
    }

    this.pendingRemoteSpecialTaps.length = 0;
    this.deferredRemoteGameplayEvents.length = 0;
    this.mirrorInput.flush();
    this.mirrorController.forceSyncRunSnapshot(snapshot.run);

    const run = this.mirrorController.run;
    if (run != null) {
      const mirrorView = this.mirrorController.getViewModel();
      this.matchState.opponentStats.score = Math.max(this.matchState.opponentStats.score, run.totalScore);
      this.matchState.opponentStats.level = Math.max(this.matchState.opponentStats.level, run.level);
      const scoreTarget = versusScoreTargetForLevel(run.level);
      this.matchState.opponentStats.levelProgressPct = clamp01(run.levelScore / Math.max(1, scoreTarget));
      this.matchState.opponentStats.doomPct = 1 - Math.max(0, Math.min(1, run.doomClockSec / doomClockCapForLevel(run.level)));
      this.matchState.opponentStats.groovePct = run.groove.groove;
      this.matchState.opponentStats.grooveActive = run.groove.active;
      this.matchState.opponentStats.grooveReady = run.groove.armed && !run.groove.active && run.groove.readyWindowSec > 0;
      this.matchState.opponentStats.pendingRotation = mirrorView.pendingRotation;
      this.matchState.opponentStats.rotationKeys = Math.max(0, Math.floor(run.rotationKeys));
      this.matchState.opponentStats.cash = run.cashBalance;
      this.matchState.opponentStats.powerups = sanitizePowerupInventory(run.powerupInventory);
      if (run.doomClockSec <= 0) {
        this.registerRemoteDoomDeath(run.totalScore);
      }
    }

    const lastGameplaySeq = Math.max(0, Math.floor(snapshot.lastGameplaySeq ?? 0));
    if (lastGameplaySeq > 0) {
      if (this.expectedRemoteGameplaySeq <= lastGameplaySeq) {
        this.expectedRemoteGameplaySeq = lastGameplaySeq + 1;
      }
      for (const seq of [...this.remoteGameplayBuffer.keys()]) {
        if (seq <= lastGameplaySeq) {
          this.remoteGameplayBuffer.delete(seq);
        }
      }
    }

    this.flushRemoteGameplayBuffer();
    const lastTrapSeq = Math.max(0, Math.floor(snapshot.lastTrapSeq ?? 0));
    const recentTraps = (snapshot.recentTraps ?? [])
      .filter((trap): trap is TrapSendPayload =>
        trap != null
        && typeof trap === "object"
        && typeof trap.trap === "string"
        && Number.isFinite(trap.trapSeq)
      )
      .sort((a, b) => a.trapSeq - b.trapSeq);
    if (recentTraps.length > 0) {
      for (const trap of recentTraps) {
        if (trap.trapSeq < this.expectedRemoteTrapSeq || trap.trapSeq > lastTrapSeq) {
          continue;
        }
        this.applyRemoteTrapPayload(
          trap,
          {
            type: "TRAP_SEND",
            seq: trap.trapSeq,
            ts: Date.now(),
            playerId: this.matchState.remotePlayerId,
            payload: trap
          },
          true
        );
        this.expectedRemoteTrapSeq = Math.max(this.expectedRemoteTrapSeq, trap.trapSeq + 1);
      }
    }
    if (this.expectedRemoteTrapSeq <= lastTrapSeq) {
      this.expectedRemoteTrapSeq = lastTrapSeq + 1;
      this.lastAppliedRemoteTrapSeq = Math.max(this.lastAppliedRemoteTrapSeq, lastTrapSeq);
    }
    for (const seq of [...this.remoteTrapBuffer.keys()]) {
      if (seq <= lastTrapSeq) {
        this.remoteTrapBuffer.delete(seq);
      }
    }
    this.flushRemoteTrapBuffer();
  }

  private handleChannelStatusChanged(status: string): void {
    if (status === "SUBSCRIBED") {
      if (this.matchState.syncHealth === "RECONNECTING") {
        this.setSyncHealth("DEGRADED");
      }
      return;
    }
    if (status === "CHANNEL_ERROR") {
      this.setSyncHealth("DEGRADED");
      return;
    }
    if (status === "TIMED_OUT" || status === "CLOSED") {
      this.setSyncHealth("RECONNECTING");
    }
  }

  private handleSendFailure(_reason: string): void {
    this.matchState.syncMetrics.sendFailCount += 1;
    if (this.matchState.syncMetrics.sendFailCount >= SYNC_SEND_FAILURE_DEGRADED_THRESHOLD) {
      this.setSyncHealth("DEGRADED");
    }
  }

  private updateSyncHealthStability(dt: number): void {
    if (this.matchState.syncHealth === "HEALTHY") {
      return;
    }
    if (!this.matchState.opponentStats.connected || this.disconnectTimer != null) {
      this.stableHealthTimerSec = 0;
      return;
    }
    this.stableHealthTimerSec += dt;
    if (this.stableHealthTimerSec >= SYNC_STABLE_WINDOW_SEC) {
      this.setSyncHealth("HEALTHY");
    }
  }

  private setSyncHealth(next: VersusMatchState["syncHealth"]): void {
    if (this.matchState.syncHealth === next) {
      return;
    }
    this.matchState.syncHealth = next;
    this.stableHealthTimerSec = 0;
    if (next === "HEALTHY") {
      this.matchState.syncMetrics.resyncCount = 0;
      this.matchState.syncMetrics.sendFailCount = 0;
    }
  }

  private recordLocalTrap(payload: TrapSendPayload): void {
    this.localRecentTraps.push(payload);
    if (this.localRecentTraps.length > RECENT_TRAP_SNAPSHOT_MAX) {
      this.localRecentTraps.splice(0, this.localRecentTraps.length - RECENT_TRAP_SNAPSHOT_MAX);
    }
  }

  private recordLocalGameplaySeq(envelope: GameEventEnvelope | null): void {
    const seq = envelope?.gameplaySeq;
    if (seq == null) {
      return;
    }
    this.localGameplaySeq = Math.max(this.localGameplaySeq, Math.floor(seq));
  }

  private checkDeath(): void {
    const localRun = this.localController.run;
    const remoteRun = this.mirrorController.run;
    const localJustDied = localRun != null && localRun.doomClockSec <= 0 && !this.localDoomDead;
    const remoteJustDied = remoteRun != null && remoteRun.doomClockSec <= 0 && !this.remoteDoomDead;

    if (localRun != null && localRun.doomClockSec <= 0) {
      const finalScore = this.captureLocalDoomFinalScore();
      if (!this.localDoomDeathSent) {
        const seq = this.sync?.sendEvent("DOOM_DEATH", { finalScore } satisfies DoomDeathPayload);
        if (seq != null) {
          this.recordLocalGameplaySeq(seq);
        }
        this.sendSnapshot();
        this.localDoomDeathSent = true;
      }
    }

    if (remoteRun != null && remoteRun.doomClockSec <= 0) {
      this.captureRemoteDoomFinalScore(remoteRun.totalScore);
    }

    if (localJustDied && remoteJustDied && this.matchState.suddenDeath == null) {
      this.localDoomDead = true;
      this.remoteDoomDead = true;
      this.resolveSimultaneousDoomDeath();
      return;
    }
    if (localJustDied) {
      this.registerLocalDoomDeath();
    }
    if (remoteJustDied) {
      this.registerRemoteDoomDeath();
    }
  }

  private syncOpponentStatsFromMirror(): void {
    const mirrorRun = this.mirrorController.run;
    if (mirrorRun == null) {
      return;
    }
    const mirrorView = this.mirrorController.getViewModel();
    if (mirrorRun.totalScore > this.matchState.opponentStats.score) {
      this.matchState.opponentStats.score = mirrorRun.totalScore;
    }
    if (mirrorRun.level > this.matchState.opponentStats.level) {
      this.matchState.opponentStats.level = mirrorRun.level;
    }
    const scoreTarget = versusScoreTargetForLevel(mirrorRun.level);
    this.matchState.opponentStats.levelProgressPct = clamp01(mirrorRun.levelScore / Math.max(1, scoreTarget));
    this.matchState.opponentStats.doomPct = 1 - Math.max(0, Math.min(1, mirrorRun.doomClockSec / doomClockCapForLevel(mirrorRun.level)));
    this.matchState.opponentStats.groovePct = mirrorRun.groove.groove;
    this.matchState.opponentStats.grooveActive = mirrorRun.groove.active;
    this.matchState.opponentStats.grooveReady = mirrorRun.groove.armed && !mirrorRun.groove.active && mirrorRun.groove.readyWindowSec > 0;
    this.matchState.opponentStats.pendingRotation = mirrorView.pendingRotation;
    this.matchState.opponentStats.rotationKeys = Math.max(0, Math.floor(mirrorRun.rotationKeys));
    this.matchState.opponentStats.cash = mirrorRun.cashBalance;
    this.matchState.opponentStats.powerups = sanitizePowerupInventory(mirrorRun.powerupInventory);
    if (this.remoteDoomDead) {
      this.captureRemoteDoomFinalScore(mirrorRun.totalScore);
    }
  }

  private bestKnownRemoteScore(): number {
    const mirrorScore = this.mirrorController.run?.totalScore ?? 0;
    return Math.max(this.matchState.opponentStats.score, mirrorScore, this.remoteDoomFinalScore ?? 0);
  }

  private bestKnownLocalScore(): number {
    const localScore = this.localController.run?.totalScore ?? 0;
    return Math.max(localScore, this.localDoomFinalScore ?? 0);
  }

  private bestKnownRemoteLevel(): number {
    const mirrorLevel = this.mirrorController.run?.level ?? 1;
    return Math.max(this.matchState.opponentStats.level, mirrorLevel);
  }

  private captureLocalDoomFinalScore(): number {
    const score = Math.max(0, Math.floor(this.localController.run?.totalScore ?? 0));
    this.localDoomFinalScore = Math.max(this.localDoomFinalScore ?? 0, score);
    return this.localDoomFinalScore;
  }

  private captureRemoteDoomFinalScore(score?: number): number {
    const payloadScore = Number.isFinite(score) ? Math.max(0, Math.floor(score ?? 0)) : 0;
    const bestKnown = this.bestKnownRemoteScore();
    this.remoteDoomFinalScore = Math.max(this.remoteDoomFinalScore ?? 0, payloadScore, bestKnown);
    this.matchState.opponentStats.score = Math.max(
      this.matchState.opponentStats.score,
      this.remoteDoomFinalScore
    );
    if (this.matchState.suddenDeath?.leaderPlayerId === this.matchState.remotePlayerId) {
      this.matchState.suddenDeath.targetScore = Math.max(
        this.matchState.suddenDeath.targetScore,
        this.remoteDoomFinalScore
      );
    }
    return this.remoteDoomFinalScore;
  }

  private registerLocalDoomDeath(): void {
    const finalScore = this.captureLocalDoomFinalScore();
    if (this.localDoomDead) {
      return;
    }
    this.localDoomDead = true;
    this.handleRegisteredDoomDeath(this.matchState.localPlayerId, finalScore);
  }

  private registerRemoteDoomDeath(finalScore?: number): void {
    const resolvedScore = this.captureRemoteDoomFinalScore(finalScore);
    if (this.remoteDoomDead) {
      if (this.matchState.suddenDeath != null) {
        this.handleRegisteredDoomDeath(this.matchState.remotePlayerId, resolvedScore);
      }
      return;
    }
    this.remoteDoomDead = true;
    this.handleRegisteredDoomDeath(this.matchState.remotePlayerId, resolvedScore);
  }

  private handleRegisteredDoomDeath(deadPlayerId: string, deadScore: number): void {
    if (this.ended) {
      return;
    }
    const suddenDeath = this.matchState.suddenDeath;
    if (suddenDeath != null) {
      const survivorDied = deadPlayerId === suddenDeath.survivorPlayerId;
      if (!survivorDied) {
        return;
      }
      this.resolveSuddenDeathByScore(deadPlayerId, deadScore);
      return;
    }

    const deadWasLocal = deadPlayerId === this.matchState.localPlayerId;
    const survivorId = deadWasLocal
      ? this.matchState.remotePlayerId
      : this.matchState.localPlayerId;
    const survivorScore = deadWasLocal
      ? this.bestKnownRemoteScore()
      : this.bestKnownLocalScore();

    if (deadScore > survivorScore) {
      this.startSuddenDeath(deadPlayerId, survivorId, deadScore);
      return;
    }
    this.endMatch(survivorId, "DOOM_DEATH");
  }

  private startSuddenDeath(leaderPlayerId: string, survivorPlayerId: string, targetScore: number): void {
    this.matchState.suddenDeath = {
      remainingSec: 30,
      leaderPlayerId,
      survivorPlayerId,
      targetScore
    };
  }

  private tickSuddenDeath(dt: number): void {
    if (this.ended || this.matchState.suddenDeath == null) {
      return;
    }
    const suddenDeath = this.matchState.suddenDeath;
    suddenDeath.remainingSec = Math.max(0, suddenDeath.remainingSec - dt);
    if (suddenDeath.remainingSec > 0) {
      return;
    }
    this.resolveSuddenDeathByScore(
      suddenDeath.survivorPlayerId,
      this.scoreForPlayer(suddenDeath.survivorPlayerId)
    );
  }

  private resolveSuddenDeathByScore(playerId: string, playerScore: number): void {
    const suddenDeath = this.matchState.suddenDeath;
    if (suddenDeath == null) {
      return;
    }
    const survivorWon = playerId === suddenDeath.survivorPlayerId && playerScore > suddenDeath.targetScore;
    const winnerId = survivorWon
      ? suddenDeath.survivorPlayerId
      : suddenDeath.leaderPlayerId;
    this.endMatch(winnerId, "DOOM_DEATH");
  }

  private resolveSimultaneousDoomDeath(): void {
    const localScore = this.captureLocalDoomFinalScore();
    const remoteScore = this.captureRemoteDoomFinalScore();
    if (localScore > remoteScore) {
      this.endMatch(this.matchState.localPlayerId, "DOOM_DEATH");
      return;
    }
    if (remoteScore > localScore) {
      this.endMatch(this.matchState.remotePlayerId, "DOOM_DEATH");
      return;
    }
    this.endMatch(null, "DOOM_DEATH");
  }

  private scoreForPlayer(playerId: string): number {
    if (playerId === this.matchState.localPlayerId) {
      return this.bestKnownLocalScore();
    }
    return this.bestKnownRemoteScore();
  }

  private endMatch(winnerId: string | null, result: string): void {
    if (this.ended) return;
    this.ended = true;
    clearActiveMatch();
    this.matchState.suddenDeath = null;
    this.matchState.winnerId = winnerId;
    this.matchState.result = result as never;
    this.sync?.unsubscribe();
    this.callbacks.onMatchEnd(winnerId, result);
  }
}

function powerupKindToArmAction(kind: string): UiAction | null {
  switch (kind) {
    case "HINT":
      return "ARM_POWERUP_HINT";
    case "ROWRIP":
      return "ARM_POWERUP_ROWRIP";
    case "COLCUT":
      return "ARM_POWERUP_COLCUT";
    case "TRANSMUTE":
      return "ARM_POWERUP_TRANSMUTE";
    case "ZIGZAGBOLT":
      return "ARM_POWERUP_ZIGZAGBOLT";
    default:
      return null;
  }
}

function createEmptyPowerupInventory() {
  return {
    HINT: 0,
    ROWRIP: 0,
    COLCUT: 0,
    TRANSMUTE: 0,
    ZIGZAGBOLT: 0
  };
}

function sanitizePowerupInventory(source: unknown) {
  const base = createEmptyPowerupInventory();
  if (source == null || typeof source !== "object") {
    return base;
  }
  const typed = source as Record<string, unknown>;
  return {
    HINT: Math.max(0, Math.floor(Number(typed.HINT ?? 0))),
    ROWRIP: Math.max(0, Math.floor(Number(typed.ROWRIP ?? 0))),
    COLCUT: Math.max(0, Math.floor(Number(typed.COLCUT ?? 0))),
    TRANSMUTE: Math.max(0, Math.floor(Number(typed.TRANSMUTE ?? 0))),
    ZIGZAGBOLT: Math.max(0, Math.floor(Number(typed.ZIGZAGBOLT ?? 0)))
  };
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function quantize01(value: number, step = 0.01): number {
  const clamped = clamp01(value);
  if (step <= 0) {
    return clamped;
  }
  return Math.round(clamped / step) * step;
}
